'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FlexibleVariantSelector } from '@/components/flexible-variant-selector';
import { FoodProductSelector } from '@/components/food-product-selector';
import { AddToCartButton } from '@/components/add-to-cart-button';
import { type Product, type ProductVariantOption } from '@/lib/productService';
import { buildVariantDetails } from '@/lib/variantDetails';
import { buildStoreThemeVariables } from '@/lib/store-theme';

type ProductVariantDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product;
  storeId: string;
  publicStoreId?: string;
  locale: string;
};

export function ProductVariantDialog({
  open,
  onOpenChange,
  product,
  storeId,
  publicStoreId,
  locale,
}: ProductVariantDialogProps) {
  const t = useTranslations('product');
  const [storeTheme, setStoreTheme] = useState<React.CSSProperties | undefined>(undefined);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariantOption | null>(null);
  const [foodSelections, setFoodSelections] = useState<{
    optionGroups: Array<{ groupId: string; optionIds: string[] }>;
    additionals: Array<{ additionalId: string; quantity: number }>;
    totalPrice: number;
    originalTotal?: number;
  } | null>(null);

  const isFoodProduct = product.catalogType === 'FOOD';
  const hasOptionGroups = isFoodProduct && product.optionGroups && product.optionGroups.length > 0;
  const hasAdditionals = isFoodProduct && product.additionals?.some((additional) => additional.active !== false);
  const isUnlimitedStock = product.unlimitedStock === true;

  // Fetch store theme when dialog opens (dialog is in portal, outside store layout)
  useEffect(() => {
    if (!open || !storeId) return;
    let cancelled = false;
    fetch(`/api/stores/${storeId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((store: { color?: string | null } | null) => {
        if (cancelled || !store) return;
        const theme = buildStoreThemeVariables(store.color ?? null);
        setStoreTheme(theme ?? undefined);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [open, storeId]);

  // Reset selections when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedVariant(null);
      setFoodSelections(null);
    }
  }, [open]);

  // Close dialog when confirmation dialog closes
  const handleConfirmationDialogClose = () => {
    onOpenChange(false);
    setSelectedVariant(null);
    setFoodSelections(null);
  };

  const hasVariants = !isFoodProduct && product.variants && product.variants.length > 0;

  // Get the effective price based on selected variant or food selections
  let basePrice = product.price;
  if (selectedVariant && hasVariants) {
    basePrice = (selectedVariant as any).price || product.price;
  } else if (foodSelections) {
    basePrice = foodSelections.totalPrice;
  }

  // When food selections exist, totalPrice already includes promotion + modifiers
  const effectivePrice = foodSelections
    ? foodSelections.totalPrice
    : (product.promotionPrice || basePrice);

  const effectiveStock = selectedVariant && hasVariants
    ? selectedVariant.stock
    : product.stock;

  // Get variant ID and details for AddToCartButton
  const variantId = selectedVariant ? (selectedVariant as any).id : undefined;
  const variantDetails = buildVariantDetails(
    selectedVariant as Record<string, unknown> | null | undefined
  );

  // Check if Food product selections are valid
  const isFoodSelectionValid = () => {
    if (!isFoodProduct || !hasOptionGroups) return true;

    if (!product.optionGroups) return true;

    return product.optionGroups.every(group => {
      const selections = foodSelections?.optionGroups.find(g => g.groupId === group.id)?.optionIds || [];
      if (group.mandatory && selections.length === 0) return false;
      if (selections.length < group.minSelections) return false;
      if (group.maxSelections && selections.length > group.maxSelections) return false;
      return true;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${isFoodProduct ? 'sm:max-w-2xl' : 'sm:max-w-lg'} max-h-[90vh] overflow-y-auto rounded-2xl`}
        style={storeTheme}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">{product.name}</DialogTitle>
          <DialogDescription>
            {isFoodProduct ? t('customizeYourOrder') : t('selectAttributes')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Variant Selector - for FASHION catalog */}
          {hasVariants && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('attributes')}</h3>
              <FlexibleVariantSelector
                variants={product.variants!}
                unlimitedStock={isUnlimitedStock}
                onVariantChange={setSelectedVariant}
              />
            </div>
          )}

          {/* Food Product Selector - for FOOD catalog */}
          {isFoodProduct && (hasOptionGroups || hasAdditionals) && (
            <FoodProductSelector
              optionGroups={product.optionGroups || []}
              additionals={product.additionals || []}
              basePrice={product.promotionPrice || product.price}
              originalBasePrice={product.promotionPrice ? product.price : undefined}
              locale={locale}
              onSelectionChange={setFoodSelections}
            />
          )}

          {/* Stock status */}
          {effectiveStock !== undefined && !isFoodProduct && (
            <div className={`text-sm font-semibold ${isUnlimitedStock || effectiveStock > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {(isUnlimitedStock || effectiveStock > 0) ? t('inStock') : t('outOfStock')}
            </div>
          )}
        </div>

        <DialogFooter>
          <AddToCartButton
            product={{
              id: product.id,
              name: product.name,
              price: effectivePrice,
              imageUrl: product.imageUrl,
              isAvailable: product.active,
            }}
            variantId={variantId}
            variantDetails={variantDetails}
            foodSelections={foodSelections ? {
              optionGroups: foodSelections.optionGroups,
              additionals: foodSelections.additionals,
            } : undefined}
            storeId={storeId}
            publicStoreId={publicStoreId}
            locale={locale}
            label={t('addToCart')}
            unavailableLabel={t('unavailable')}
            successMessage={t('addedToCart')}
            showDialog={true}
            dialogTranslations={{
              title: t('addedToCart'),
              goToCart: t('goToCart'),
              continueShopping: t('continueShopping'),
            }}
            disabled={
              !product.active ||
              (!isFoodProduct && !isUnlimitedStock && effectiveStock === 0) ||
              (hasVariants && !selectedVariant) ||
              (isFoodProduct && hasOptionGroups && !isFoodSelectionValid())
            }
            onDialogClose={handleConfirmationDialogClose}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
